/**
 * Admin Approval History — every request THIS faculty has approved or
 * rejected. Filters: status, free-text (student / patient / component).
 *
 * Reads `GET /api/orders/approval-history` (already scoped server-side
 * to `approved_by == current_user.id`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Search } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { shortRef } from '../ui';

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function HistoryPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | approved | rejected
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? '' : `?status_filter=${statusFilter}`;
      const { data } = await apiClient.get(`${endpoints.orders.approvalHistory}${params}`);
      setRows(Array.isArray(data?.orders) ? data.orders : []);
    } catch (e) {
      console.error('approval-history', e);
      showToast && showToast('Failed to load approval history', 'err');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.student_name, r.patient_name, r.patient_pid,
        ...(r.items || []).map((it) => `${it.component_name} ${it.brand_name}`),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const counts = useMemo(() => ({
    total: rows.length,
    approved: rows.filter((r) => r.status === 'approved').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
  }), [rows]);

  return (
    <div data-testid="ap-history-page">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">My Approval History</h1>
      <p className="ap-page-lede">
        Every request you have approved or rejected — most recent first. Search by student, patient, PID, or component name.
      </p>

      <div className="ap-ind-row" role="group" aria-label="History summary">
        <div className="ap-ind" data-testid="ap-history-count-total">
          <div className="ap-ind-label">Total Actions</div>
          <div className="ap-ind-value">{counts.total}</div>
          <div className="ap-ind-hint">Approved + rejected together</div>
        </div>
        <div className="ap-ind" data-testid="ap-history-count-approved">
          <div className="ap-ind-label">Approved</div>
          <div className="ap-ind-value">{counts.approved}</div>
          <div className="ap-ind-hint">Requests you authorised</div>
        </div>
        <div className="ap-ind" data-testid="ap-history-count-rejected">
          <div className="ap-ind-label"><em className="is-warn" />Rejected</div>
          <div className="ap-ind-value">{counts.rejected}</div>
          <div className="ap-ind-hint">Requests you declined</div>
        </div>
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 420 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, opacity: 0.5 }} />
          <input
            className="ap-input"
            style={{ paddingLeft: 30 }}
            placeholder="Search student, patient, PID, component…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ap-history-search"
          />
        </div>
        <select
          className="ap-input"
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="ap-history-status-filter"
        >
          <option value="all">All actions</option>
          <option value="approved">Approved only</option>
          <option value="rejected">Rejected only</option>
        </select>
      </div>

      <div className="ap-mt-6">
        {loading ? (
          <div className="ap-empty">Loading history…</div>
        ) : filtered.length === 0 ? (
          <div className="ap-empty" data-testid="ap-history-empty">
            {rows.length === 0 ? (
              <p>You haven’t approved or rejected any requests yet.</p>
            ) : (
              <p>No history rows match your filters.</p>
            )}
          </div>
        ) : (
          <div className="ap-table-wrap" data-testid="ap-history-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>When</th>
                  <th>Student</th>
                  <th>Patient · PID</th>
                  <th>Items</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} data-testid={`ap-history-row-${o.id}`}>
                    <td>
                      {o.status === 'approved' ? (
                        <span style={{ color: '#0F766E', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <CheckCircle2 size={14} /> Approved
                        </span>
                      ) : (
                        <span style={{ color: '#B04A3C', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <XCircle size={14} /> Rejected
                        </span>
                      )}
                      <div className="ap-subtle" style={{ fontSize: 11 }}>{shortRef(o.id, 'REQ')}</div>
                    </td>
                    <td>{fmt(o.approved_at)}</td>
                    <td>{o.student_name || '—'}</td>
                    <td>
                      <div>{o.patient_name || '—'}</div>
                      <div className="ap-subtle">{o.patient_pid || '—'}</div>
                    </td>
                    <td>
                      {(o.items || []).slice(0, 3).map((it, i) => (
                        <div key={i} style={{ fontSize: 13 }}>
                          {it.component_name} · <span className="ap-subtle">{it.brand_name || '—'}</span> × {it.quantity}
                        </div>
                      ))}
                      {(o.items || []).length > 3 && (
                        <div className="ap-subtle" style={{ fontSize: 12 }}>+{(o.items || []).length - 3} more…</div>
                      )}
                    </td>
                    <td className="ap-subtle" style={{ maxWidth: 260 }}>{o.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
